
import React, { useEffect, useState } from 'react';
import { Layout, Menu, Card, Table, Tag, Button, Statistic, Row, Col, Typography, message, Tabs, Form, Input, Divider, Modal } from 'antd';
import { 
  DashboardOutlined, 
  UserOutlined, 
  SafetyCertificateOutlined, 
  LogoutOutlined,
  SettingOutlined,
  PayCircleOutlined,
  SaveOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import analysisApi, { OrderEvent, SalesStatisticsResponse, StockPopularityResponse } from '../../services/api';

const { Header, Content, Sider } = Layout;
const { Title } = Typography;
const { TabPane } = Tabs;

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<OrderEvent[]>([]);
  const [salesStats, setSalesStats] = useState<SalesStatisticsResponse | null>(null);
  const [popularityStats, setPopularityStats] = useState<StockPopularityResponse[]>([]);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [selectedKey, setSelectedKey] = useState('1');
  
  // Settings State
  const [settingsForm] = Form.useForm();
  const [settingsLoading, setSettingsLoading] = useState(false);

  // User Management State
  const [users, setUsers] = useState<any[]>([]);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [userForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  // Audit Logs State
  const [auditLogs, setAuditLogs] = useState<any[]>([]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await analysisApi.getCurrentUser();
        setUserInfo(user);
        
        if (user.role !== 'admin') {
          message.error('无权访问管理后台');
          navigate('/');
        }
      } catch (error) {
        // Dev mode bypass for local testing if API fails
        if (process.env.NODE_ENV === 'development') {
           setUserInfo({ username: 'Admin(Dev)', role: 'admin' });
        } else {
           message.error('请先登录');
           navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, [navigate]);

  // Load Settings when tab changes
  useEffect(() => {
    if (selectedKey === '4') {
      loadSettings();
    }
    if (selectedKey === '2') {
      loadUsers();
    }
    if (selectedKey === '3') {
      loadAuditLogs();
    }
  }, [selectedKey]);

  const loadUsers = async () => {
    try {
      const data = await analysisApi.getAllUsers();
      setUsers(data);
    } catch (e) {
      message.error('加载用户列表失败');
    }
  };

  const loadAuditLogs = async () => {
    try {
      const data = await analysisApi.getAuditLogs(100);
      setAuditLogs(data);
    } catch (e) {
      message.error('加载审计日志失败');
    }
  };

  const loadSettings = async () => {
    setSettingsLoading(true);
    try {
      const data = await analysisApi.getSystemSettings();
      settingsForm.setFieldsValue({
        payment_price: data.payment_price?.value,
        payment_merchant_id: data.payment_merchant_id?.value,
        tushare_token: data.tushare_token?.value,
      });
    } catch (e) {
      message.error('加载设置失败');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSaveSettings = async (values: any) => {
    setSettingsLoading(true);
    try {
      const settingsToUpdate = Object.entries(values).map(([key, value]) => ({
        key,
        value: String(value)
      }));
      await analysisApi.updateSystemSettings(settingsToUpdate);
      message.success('系统设置已保存');
    } catch (e) {
      message.error('保存设置失败');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      const values = await userForm.validateFields();
      await analysisApi.createUser(values);
      message.success('用户创建成功');
      setUserModalVisible(false);
      userForm.resetFields();
      loadUsers();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '创建失败');
    }
  };

  const handleUpdatePassword = async () => {
    try {
      const values = await passwordForm.validateFields();
      if (values.new_password !== values.confirm_password) {
        message.error('两次密码输入不一致');
        return;
      }
      await analysisApi.updateMyPassword({
        old_password: values.old_password,
        new_password: values.new_password
      });
      message.success('密码修改成功');
      setPasswordModalVisible(false);
      passwordForm.resetFields();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '密码修改失败');
    }
  };

  const userColumns = [
    { title: '用户名', dataIndex: 'username', key: 'username' },
    { title: '角色', dataIndex: 'role', key: 'role', render: (t: string) => <Tag color={t==='admin'?'gold':'blue'}>{t}</Tag> },
    { title: '状态', dataIndex: 'disabled', key: 'disabled', render: (d: boolean) => <Tag color={d?'red':'green'}>{d?'禁用':'正常'}</Tag> },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', render: (t: string) => t?.split('T')[0] }
  ];

  const auditColumns = [
    { title: '时间', dataIndex: 'created_at', key: 'created_at', width: 180, render: (t: string) => t.replace('T', ' ').split('.')[0] },
    { title: '用户', dataIndex: 'user', key: 'user' },
    { title: '操作类型', dataIndex: 'action', key: 'action', render: (t: string) => <Tag>{t}</Tag> },
    { title: '详情', dataIndex: 'detail', key: 'detail' },
    { title: 'IP地址', dataIndex: 'ip_address', key: 'ip_address' }
  ];

  // 轮询订单事件与统计数据
  useEffect(() => {
    let interval: any;
    
    const fetchData = async () => {
      try {
        // 并行请求事件日志、销售统计、热度排行
        const [eventsRes, salesRes, popularityRes] = await Promise.all([
          analysisApi.getOrderEvents(50),
          analysisApi.getSalesStatistics(),
          analysisApi.getStockPopularity(10)
        ]);
        
        if (eventsRes && eventsRes.events) {
          setEvents(eventsRes.events);
        }
        if (salesRes) {
          setSalesStats(salesRes);
        }
        if (popularityRes) {
          setPopularityStats(popularityRes);
        }
      } catch (e) {
        console.error("Failed to fetch dashboard data", e);
      }
    };

    if (userInfo && autoRefresh) {
      fetchData();
      interval = setInterval(fetchData, 5000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [userInfo, autoRefresh]);

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    message.success('已退出登录');
    navigate('/login');
  };

  // 2. 按客户订单ID归类
  // 我们只展示最近的事件，所以直接用表格展示流水即可，或者按 order_id 分组
  // 这里展示原始事件流水更直观，或者分组展示
  
  const eventColumns = [
    {
      title: '时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (t: string) => t.replace('T', ' ').split('.')[0]
    },
    {
      title: '股票代码',
      dataIndex: 'symbol',
      key: 'symbol',
      render: (t: string) => <Tag color="blue">{t}</Tag>
    },
    {
      title: '订单ID',
      dataIndex: 'order_id',
      key: 'order_id',
      ellipsis: true,
    },
    {
      title: '事件类型',
      dataIndex: 'event',
      key: 'event',
      render: (t: string) => {
        let color = 'default';
        if (t === 'created') color = 'blue';
        if (t === 'paid') color = 'green';
        if (t === 'expired') color = 'orange';
        if (t === 'failed') color = 'red';
        return <Tag color={color}>{t.toUpperCase()}</Tag>;
      }
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (v: number) => `¥${v}`
    },
    {
      title: '详情',
      dataIndex: 'payload',
      key: 'payload',
      ellipsis: true
    }
  ];

  const stockColumns = [
    { title: '排名', key: 'rank', render: (_: any, __: any, index: number) => index + 1, width: 60 },
    { title: '股票代码', dataIndex: 'symbol', key: 'symbol', render: (t: string) => <Tag color="geekblue">{t}</Tag> },
    { title: '查询/订单次数', dataIndex: 'count', key: 'count', sorter: (a: any, b: any) => a.count - b.count }
  ];

  if (loading) {
    return <div style={{ padding: 50, textAlign: 'center' }}>加载中...</div>;
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider theme="dark" collapsible>
        <div style={{ 
          height: 32, 
          margin: 16, 
          background: 'rgba(255, 255, 255, 0.2)', 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '13px',
          borderRadius: '4px'
        }}>
          CHEESESMART 知是
        </div>
        <Menu theme="dark" selectedKeys={[selectedKey]} mode="inline" onClick={(e) => setSelectedKey(e.key)}>
          <Menu.Item key="1" icon={<DashboardOutlined />}>
            系统概览
          </Menu.Item>
          <Menu.Item key="2" icon={<UserOutlined />}>
            用户管理
          </Menu.Item>
          <Menu.Item key="3" icon={<SafetyCertificateOutlined />}>
            安全审计
          </Menu.Item>
          <Menu.Item key="4" icon={<SettingOutlined />}>
            系统设置
          </Menu.Item>
          <Menu.Item key="5" icon={<PayCircleOutlined />}>
            订单监控
          </Menu.Item>
        </Menu>
      </Sider>
      <Layout className="site-layout">
        <Header className="site-layout-background" style={{ padding: '0 24px', background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>管理控制台</Title>
          <div>
            <span style={{ marginRight: 16 }}>欢迎, {userInfo?.username}</span>
            <Button type="link" onClick={() => setPasswordModalVisible(true)}>修改密码</Button>
            <Button type="link" icon={<LogoutOutlined />} onClick={handleLogout}>退出</Button>
          </div>
        </Header>
        <Content style={{ margin: '0 16px' }}>
          <div style={{ padding: 24, minHeight: 360 }}>
            
            {selectedKey === '1' && (
              <div style={{ marginBottom: 24 }}>
                {/* 关键指标行 - 第一行：今日与本周 */}
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={6}>
                    <Card>
                      <Statistic 
                        title="今日订单数" 
                        value={salesStats?.today.count || 0} 
                        valueStyle={{ color: '#3f8600' }}
                        suffix={`/ ¥${(salesStats?.today.amount || 0).toFixed(2)}`}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card>
                      <Statistic 
                        title="本周成交额" 
                        value={salesStats?.week.amount || 0} 
                        precision={2} 
                        prefix="¥"
                        suffix={`(${salesStats?.week.count || 0}单)`}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card>
                      <Statistic 
                        title="本月成交额" 
                        value={salesStats?.month.amount || 0} 
                        precision={2} 
                        prefix="¥"
                        suffix={`(${salesStats?.month.count || 0}单)`}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card>
                      <Statistic 
                        title="累计总成交额" 
                        value={salesStats?.total.amount || 0} 
                        precision={2} 
                        prefix="¥" 
                        valueStyle={{ color: '#cf1322' }}
                      />
                    </Card>
                  </Col>
                </Row>
              </div>
            )}

            {(selectedKey === '1' || selectedKey === '5') && (
              <Row gutter={16}>
                <Col span={16}>
                  <Card title={
                    <div style={{display: 'flex', justifyContent: 'space-between'}}>
                      <span>实时订单流水 (最近50条)</span>
                      <Button size="small" type={autoRefresh ? 'primary' : 'default'} onClick={() => setAutoRefresh(!autoRefresh)}>
                        {autoRefresh ? '自动刷新中' : '暂停刷新'}
                      </Button>
                    </div>
                  }>
                    <Table 
                      dataSource={events} 
                      columns={eventColumns} 
                      rowKey={(r) => r.timestamp + r.event + r.order_id} 
                      pagination={{ pageSize: 10 }} 
                      size="small"
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card title="股票热度排行榜 (历史累计)">
                    <Table 
                      dataSource={popularityStats} 
                      columns={stockColumns} 
                      rowKey="symbol" 
                      pagination={false} 
                      size="small"
                    />
                  </Card>
                </Col>
              </Row>
            )}
            
            {selectedKey === '2' && (
              <Card 
                title="用户管理" 
                extra={<Button type="primary" onClick={() => setUserModalVisible(true)}>新增管理员</Button>}
              >
                <Table dataSource={users} columns={userColumns} rowKey="username" />
              </Card>
            )}

            {selectedKey === '3' && (
              <Card title="安全审计日志" extra={<Button onClick={loadAuditLogs}>刷新</Button>}>
                <Table dataSource={auditLogs} columns={auditColumns} rowKey="id" />
              </Card>
            )}
            
            {selectedKey === '4' && (
              <Card 
                title="系统参数配置" 
                extra={
                  <Button icon={<ReloadOutlined />} onClick={loadSettings} loading={settingsLoading}>刷新</Button>
                }
              >
                <Form
                  form={settingsForm}
                  layout="vertical"
                  onFinish={handleSaveSettings}
                >
                  <Title level={5}>支付配置</Title>
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item 
                        name="payment_price" 
                        label="单次解锁价格 (元)"
                        rules={[{ required: true, message: '请输入价格' }]}
                      >
                        <Input prefix="¥" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item 
                        name="payment_merchant_id" 
                        label="收款商户账号 (Merchant ID)"
                        extra="用于更换收款主体，请确保账号在支付平台已激活"
                      >
                        <Input placeholder="输入新的商户号..." />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider />

                  <Title level={5}>数据源配置</Title>
                  <Form.Item 
                    name="tushare_token" 
                    label="Tushare API Token"
                    extra="配置全局 Tushare Token，更新后所有用户将自动使用此 Token"
                  >
                    <Input.Password placeholder="输入 Tushare Token..." />
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={settingsLoading}>
                      保存所有设置
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            )}

          </div>
        </Content>
      </Layout>

      {/* 创建用户弹窗 */}
      <Modal
        title="新增管理员"
        open={userModalVisible}
        onOk={handleCreateUser}
        onCancel={() => setUserModalVisible(false)}
      >
        <Form form={userForm} layout="vertical">
          <Form.Item name="username" label="用户名" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="password" label="密码" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="role" label="角色" initialValue="admin">
             <Input disabled />
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改密码弹窗 */}
      <Modal
        title="修改密码"
        open={passwordModalVisible}
        onOk={handleUpdatePassword}
        onCancel={() => setPasswordModalVisible(false)}
      >
        <Form form={passwordForm} layout="vertical">
          <Form.Item name="old_password" label="原密码" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="new_password" label="新密码" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="confirm_password" label="确认新密码" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default AdminDashboard;
